import { MatDialogConfig } from '@angular/material/dialog';
import { OrderColumnKey } from '../enums/order-table-columns.enum';
import { OrderField, OrderResponseDto } from '../interfaces/order.interface';
import { OrdersHistoryConfig } from '../interfaces/column-config.interface';
import { OrdersHistoryEnum, OrderStatus } from '../enums/orders-history.enum';
import { BASE_CELL_CLASS, BASE_HEADER_CLASS } from './table-style.constants';
import { SortOptionConfig } from '../interfaces/sort-option.interface';
import { IFilterOptions } from '../interfaces/orders.interface';
import { OrderDateCreatedEnum, OrderFiltersEnum } from '../enums/orders.enum';
import { OrderDeliveryTypeEnum } from '../enums/order-delivery-type-enum';

export const ORDER_HISTORY_TABLE = 'orders.editOrderStatus.form.historyTable';

export const ORDERS_GENERAL_INFO_FIELDS = 'orders.generalInfo.fields';
export const ORDERS_TABLE_HEADER_LABELS = 'orders.table.headerLabels';

export interface OrderTableColumn {
  key: OrderColumnKey;
  label: string;
  headerClass: string;
  cellClass?: string;
  sortable?: boolean;
  isFullName?: boolean;
}

export const ORDER_TABLE_COLUMNS_CONFIG: OrderTableColumn[] = [
  {
    key: OrderColumnKey.Id,
    label: `${ORDERS_TABLE_HEADER_LABELS}.id`,
    headerClass: BASE_HEADER_CLASS,
    sortable: true,
    cellClass: BASE_CELL_CLASS,
  },
  {
    key: OrderColumnKey.CreationDate,
    label: `${ORDERS_TABLE_HEADER_LABELS}.dateCreated`,
    headerClass: BASE_HEADER_CLASS,
    sortable: true,
    cellClass: BASE_CELL_CLASS,
  },
  {
    key: OrderColumnKey.CustomerFirstName,
    label: `${ORDERS_TABLE_HEADER_LABELS}.name`,
    headerClass: BASE_HEADER_CLASS,
    isFullName: true,
    cellClass: BASE_CELL_CLASS,
  },
  {
    key: OrderColumnKey.CustomerEmail,
    label: `${ORDERS_TABLE_HEADER_LABELS}.email`,
    headerClass: BASE_HEADER_CLASS,
    cellClass: BASE_CELL_CLASS,
  },
  {
    key: OrderColumnKey.DeliveryType,
    label: `${ORDERS_TABLE_HEADER_LABELS}.type`,
    headerClass: BASE_HEADER_CLASS,
    sortable: true,
    cellClass: BASE_CELL_CLASS,
  },
  {
    key: OrderColumnKey.OrderStatus,
    label: `${ORDERS_TABLE_HEADER_LABELS}.status`,
    headerClass: BASE_HEADER_CLASS,
    sortable: true,
    cellClass: BASE_CELL_CLASS,
  },
  {
    key: OrderColumnKey.Creator,
    label: `${ORDERS_TABLE_HEADER_LABELS}.creator`,
    headerClass: BASE_HEADER_CLASS,
    cellClass: BASE_CELL_CLASS,
  },
  {
    key: OrderColumnKey.Actions,
    label: `${ORDERS_TABLE_HEADER_LABELS}.actions`,
    headerClass: BASE_HEADER_CLASS,
  },
];

export const FAKE_ORDERS_QUANTITY = 5;

export const ORDER_FIELD_KEYS = {
  orderId: 'orderId',
  customerId: 'customerId',
  customerEmail: 'customerEmail',
  status: 'status',
  dateCreated: 'dateCreated',
  customerName: 'customerName',
  deliveryType: 'deliveryType',
  creator: 'creator',
} as const;

export type AllOrderFieldKeys = keyof typeof ORDER_FIELD_KEYS;

export const ORDER_GENERAL_FIELDS: OrderField[] = [
  {
    key: ORDER_FIELD_KEYS.orderId,
    label: `${ORDERS_GENERAL_INFO_FIELDS}.orderId`,
    value: (item: OrderResponseDto) => item.orderId,
  },
  {
    key: ORDER_FIELD_KEYS.dateCreated,
    label: `${ORDERS_GENERAL_INFO_FIELDS}.dateCreated`,
    value: (item: OrderResponseDto) => item.dateCreated,
  },
  {
    key: ORDER_FIELD_KEYS.customerId,
    label: `${ORDERS_GENERAL_INFO_FIELDS}.customerId`,
    value: (item: OrderResponseDto) => item.customerId,
  },
  {
    key: ORDER_FIELD_KEYS.customerName,
    label: `${ORDERS_GENERAL_INFO_FIELDS}.customerName`,
    value: (item: OrderResponseDto) => `${item.firstName} ${item.lastName}`,
  },
  {
    key: ORDER_FIELD_KEYS.customerEmail,
    label: `${ORDERS_GENERAL_INFO_FIELDS}.customerEmail`,
    value: (item: OrderResponseDto) => item.customerEmail,
  },
  {
    key: ORDER_FIELD_KEYS.deliveryType,
    label: `${ORDERS_GENERAL_INFO_FIELDS}.deliveryType`,
    value: (item: OrderResponseDto) => item.deliveryType,
  },
  {
    key: ORDER_FIELD_KEYS.status,
    label: `${ORDERS_GENERAL_INFO_FIELDS}.status`,
    value: (item: OrderResponseDto) => item.orderStatus,
  },
  {
    key: ORDER_FIELD_KEYS.creator,
    label: `${ORDERS_GENERAL_INFO_FIELDS}.creator`,
    value: (item: OrderResponseDto) => item.creator,
  },
];

export const EDIT_STATUS_DIALOG_CONFIG: MatDialogConfig = {
  panelClass: 'orders-dialog-panel',
  width: '808px',
  maxWidth: '808px',
  autoFocus: false,
  restoreFocus: false,
};

export const ORDERS_HISTORY_COLUMNS_CONFIG: readonly OrdersHistoryConfig[] = [
  {
    name: OrdersHistoryEnum.DATE,
    label: `${ORDER_HISTORY_TABLE}.date`,
    headerClass: BASE_HEADER_CLASS,
    cellClass: BASE_CELL_CLASS,
  },
  {
    name: OrdersHistoryEnum.STATUS,
    label: `${ORDER_HISTORY_TABLE}.status`,
    headerClass: BASE_HEADER_CLASS,
    cellClass: BASE_CELL_CLASS,
  },
  {
    name: OrdersHistoryEnum.CREATOR,
    label: `${ORDER_HISTORY_TABLE}.creator`,
    headerClass: BASE_HEADER_CLASS,
    cellClass: BASE_CELL_CLASS,
  },
  {
    name: OrdersHistoryEnum.COMMENT,
    label: `${ORDER_HISTORY_TABLE}.comment`,
    headerClass: BASE_HEADER_CLASS,
    cellClass: BASE_CELL_CLASS,
  },
];

export const ORDER_SORT_OPTIONS: SortOptionConfig[] = [
  {
    key: 'id-desc',
    label: 'orders.sort.idDesc',
    sorting: [{ field: 'Id', isDescending: true }],
  },
  {
    key: 'id-asc',
    label: 'orders.sort.idAsc',
    sorting: [{ field: 'Id', isDescending: false }], 
  },
  {
    key: 'date-desc',
    label: 'orders.sort.dateDesc',
    sorting: [{ field: 'DateCreated', isDescending: true }],
  },
  {
    key: 'date-asc',
    label: 'orders.sort.dateAsc',
    sorting: [{ field: 'DateCreated', isDescending: false }], //
  },
  {
    key: 'status-asc',
    label: 'orders.sort.statusAsc',
    sorting: [{ field: 'OrderStatus', isDescending: false }],
  },
  {
    key: 'status-desc',
    label: 'orders.sort.statusDesc',
    sorting: [{ field: 'OrderStatus', isDescending: true }],
  },
  {
    key: 'delivery-asc',
    label: 'orders.sort.deliveryAsc',
    sorting: [{ field: 'DeliveryType', isDescending: false }],
  },
  {
    key: 'delivery-desc',
    label: 'orders.sort.deliveryDesc',
    sorting: [{ field: 'DeliveryType', isDescending: true }],
  },
  {
    key: 'creator-asc',
    label: 'orders.sort.creatorAsc',
    sorting: [{ field: 'Creator', isDescending: false }],
  },
  {
    key: 'creator-desc',
    label: 'orders.sort.creatorDesc',
    sorting: [{ field: 'Creator', isDescending: false }],
  },
];

export const DATE_CREATED_FILTER_OPTIONS: IFilterOptions[] = [
  { key: OrderDateCreatedEnum.Today, label: 'orders.filters.dateCreated.today' },
  { key: OrderDateCreatedEnum.Yesterday, label: 'orders.filters.dateCreated.yesterday' },
  { key: OrderDateCreatedEnum.Last7days, label: 'orders.filters.dateCreated.last7days' },
  { key: OrderDateCreatedEnum.Last30days, label: 'orders.filters.dateCreated.last30days' },
  { key: OrderDateCreatedEnum.ThisMonth, label: 'orders.filters.dateCreated.thisMonth' },
  { key: OrderDateCreatedEnum.LastMonth, label: 'orders.filters.dateCreated.lastMonth' },
  { key: OrderDateCreatedEnum.Custom, label: 'orders.filters.dateCreated.customRange' },
];

export const ORDER_STATUS_FILTER_OPTIONS: IFilterOptions[] = [
  { key: OrderStatus.NEW, label: 'orders.filters.orderStatus.new' },
  { key: OrderStatus.PROCESSING, label: 'orders.filters.orderStatus.processing' },
  { key: OrderStatus.IN_PROGRESS, label: 'orders.filters.orderStatus.inProgress' },
  { key: OrderStatus.DONE, label: 'orders.filters.orderStatus.done' },
  { key: OrderStatus.CANCELLED, label: 'orders.filters.orderStatus.cancelled' },
];

export const DELIVERY_TYPE_FILTER_OPTIONS: IFilterOptions[] = [
  { key: OrderDeliveryTypeEnum.SelfDelivery, label: 'orders.filters.deliveryType.selfDelivery' },
  { key: OrderDeliveryTypeEnum.Courier, label: 'orders.filters.deliveryType.courier' },
  { key: OrderDeliveryTypeEnum.InShop, label: 'orders.filters.deliveryType.inShop' },
];

export const DATE_CREATED: OrderDateCreatedEnum[] = [
  OrderDateCreatedEnum.Yesterday,
  OrderDateCreatedEnum.Today,
  OrderDateCreatedEnum.Last7days,
  OrderDateCreatedEnum.Last30days,
  OrderDateCreatedEnum.ThisMonth,
  OrderDateCreatedEnum.LastMonth,
  OrderDateCreatedEnum.Custom,
];

export const ORDER_FILTERS_CONFIG = [
  { title: 'orders.filters.sections.dateCreated', key: OrderFiltersEnum.DATE_CREATED },
  { title: 'orders.filters.sections.deliveryType', key: OrderFiltersEnum.DELIVERY_TYPE },
  { title: 'orders.filters.sections.status', key: OrderFiltersEnum.STATUS },
  { title: 'orders.filters.sections.creator', key: OrderFiltersEnum.CREATOR },
];
